<template>
	<div class="create-edit-contact">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="create-edit-contact__header">
			<div class="create-edit-contact__header-title">
				{{ $t(props.type === 'create' ? 'contact_create_title' : 'contact_edit_title') }}
			</div>
			<app-tooltip :text="$t('close_label')">
				<template #activator>
					<app-icon
						class="create-edit-contact__header-close"
						name="close"
						width="20px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
						@click="emit('on-close')"
					/>
				</template>
			</app-tooltip>
		</div>
		<div class="create-edit-contact__body">
			<app-select
				v-if="props.type === 'create' && ctx.context === 'project'"
				v-model="selectedAttachClientId"
				:items="attachClientItems"
				class="create-edit-contact__search"
				:placeholder="$t('contact_project_client')"
			/>
			<app-input
				ref="$nameRef"
				v-model="filters.Name"
				class="create-edit-contact__search"
				:placeholder="$t('name_short')"
				:error="nameError"
				@clear-error="nameError = false"
			/>
			<app-input
				ref="$postRef"
				v-model="filters.Post"
				class="create-edit-contact__search"
				:placeholder="$t('position')"
				:error="postError"
				@clear-error="postError = false"
			/>
			<app-textarea
				class="create-edit-contact__textarea"
				v-model="filters.Contacts"
				height="100%"
				:placeholder="$t('contact_info')"
				:error="contactsError"
				@input="contactsError = false"
			/>
			<div

				:class="{
						'create-edit-contact__contact-exist': true,
						'create-edit-contact__contact-exist--show': isContactExist
					}"
				@click="showExistContacts"
			>
				<app-icon
					name="user-exist"
					size="16px"
				/>
				{{ $t('contact_maybe_exists') }}
			</div>
			<div
				:class="{
					'create-edit-contact__address': true,
					'create-edit-contact__address--error': invalidAddressLevels.length > 0
				}"
				:data-invalid-levels="invalidAddressLevelsAttr"
			>
				<inline-address-editor
					ref="$addressEditor"
					id="create-edit-contact"
					v-model="contactAddressChain"
					:menu-z-index="10010"
					:always-editing="true"
				/>
			</div>
			<div class="create-edit-contact__body-tags">
				<app-resizable-dropdown
					ref="$addTag"
					position="left"
					:close-by-click-outside="true"
					:max-width="640"
					:max-height="460"
					name="$addTagCreateEditContact"
				>
					<template #activator>
						<app-tooltip :text="$t('tags_upcase')">
							<template #activator>
								<app-icon
									class="create-edit-contact__body-tags-add"
									name="hash"
									width="16px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-tag-selector
						:model-value="contactTagsModel"
						:entity-type="TagEntityType.Person"
						mode="select"
						allow-create
						@update:model-value="onSelectorChange"
						@create-tag="onCreateTag"
						@close="$addTag?.close()"
					/>
				</app-resizable-dropdown>
				<span
					v-for="tag in filters.Tags"
					class="create-edit-contact__body-tags-item"
					:key="tag.Id"
				>
						{{ tag.Name.toLowerCase().trim() }}
						<app-tooltip :text="$t('ui_tag_detach')">
							<template #activator>
								<app-icon
									class="create-edit-contact__body-tags-item-delete"
									name="close"
									width="12px"
									color="#D2D2D2"
									hover-color="#499259"
									cursor="pointer"
									@click="onRemoveTag(tag.Id)"
								/>
							</template>
						</app-tooltip>
					</span>
			</div>
		</div>
		<div class="create-edit-contact__footer">
			<app-button
				:label="$t(props.type === 'create' ? 'create_short' : 'save')"
				@click="onSave"
				:disabled="isLoading || !isFormValid"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import {
	clientsStore,
	Client,
	NewContact,
	Tag,
	AddressChainLevel
} from "@/store/clients";
import {PersonApi} from "@/ api/PersonApi";
import { auxStore } from "@/store/auxillary";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppTextarea from "@/components/ui-kit/textarea/AppTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import InlineAddressEditor from "@/components/common/address-selector/InlineAddressEditor.vue";
import { TagEntityType, TagBaseType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { useContactsContext } from "@/composables/useContactsContext";
import { AddressInputComponentOut } from "@/models/interfaces/entity-settings";
import {
	missingRequiredAddressLevels,
	RequestAddressContainer,
	toFullRequestContainer
} from "@/composables/useAddressRequest";

type CreateEditContactProps = {
	client?: Client,
	type?: 'create' | 'edit'
}

const emit = defineEmits<{
	(e: 'on-close'): void,
	(e: 'exist-contact-string', value: string): void,
}>()

const props = withDefaults(defineProps<CreateEditContactProps>(), {
	type: 'create'
})

const $nameRef = ref(null)
const $postRef = ref(null)
const $addTag = ref<typeof AppResizableDropdown | null>(null)
const store = clientsStore()
const ctx = useContactsContext()
const selectedAttachClientId = ref<number | null>(null)
const attachClientItems = computed(() =>
	(ctx.attachClientOptions ?? []).map(c => ({ id: c.Id, label: c.Name }))
)
const isInit = ref(false)
const isLoading = ref(false)
const isContactExist = ref(false)
const isContactExistArray = ref<Client[]>([])

const nameError = ref(false)
const postError = ref(false)
const contactsError = ref(false)

const invalidAddressLevels = ref<number[]>([])
const invalidAddressLevelsAttr = computed(() => invalidAddressLevels.value.join(' '))

const filters = ref<Client | NewContact>({
	Name: '',
	Post: '',
	Contacts: '',
	InputContainer: {
		components: [],
		activeLevel: 0
	},
	Tags: []
})

const contactAddressChain = ref<RequestAddressContainer | null>(null)
const $addressEditor = ref<{ flush: () => void } | null>(null)

const addressStructure = (): AddressInputComponentOut[] =>
	(auxStore().getCachedAddressContainer ?? []) as AddressInputComponentOut[]

// Сервер ждёт в InputContainer все уровни структуры: незаполненные — с value: "".
// В ответе пустых уровней нет, поэтому добираем их из структуры перед отправкой.
const fullAddressContainer = (): RequestAddressContainer =>
	toFullRequestContainer(contactAddressChain.value, addressStructure())

const addressChainToContainer = (chain: AddressChainLevel[]): RequestAddressContainer | null => {
	const structure = addressStructure()
	const nodeByLevel = new Map(structure.map(s => [s.Level, s.AddressNodeId]))
	const components = chain
		.filter(l => (l.Name ?? "").trim().length > 0)
		.map(l => ({ addressNodeId: nodeByLevel.get(l.Level) ?? 0, level: l.Level, value: l.Name }))
	if (!components.length) {
		return null
	}

	return {
		components,
		activeLevel: Math.max(0, ...components.map(c => c.level))
	}
}

const isNameFilled = computed(() => !!filters.value.Name?.trim())
const isPostFilled = computed(() => !!filters.value.Post?.trim())
const isContactsFilled = computed(() => !!filters.value.Contacts?.trim())
const missingAddressLevels = computed(
	() => missingRequiredAddressLevels(contactAddressChain.value, addressStructure())
)

const isFormValid = computed(() =>
	isNameFilled.value
	&& isPostFilled.value
	&& isContactsFilled.value
	&& !missingAddressLevels.value.length
)

const contactTagsModel = computed<TagWithFrequency[]>(
	() => (filters.value.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = (nextTags: TagWithFrequency[]) => {
	filters.value.Tags = nextTags as unknown as Tag[]
}

const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase()
	if (!normalized) {
		return
	}
	const current = (filters.value.Tags ?? []) as Tag[]
	if (current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1
	filters.value.Tags = [...current, { Id: tempId, Name: normalized, Type: TagBaseType.Common }]
}

const onRemoveTag = (tagId: number) => {
	filters.value.Tags = (filters.value.Tags as Tag[]).filter(t => t.Id !== tagId)
}

const onSave = async () => {
	try {
		isLoading.value = true
		$addressEditor.value?.flush()
		if (!checkErrors()) {
			if (props.type === 'create') {
				localStorage.removeItem('newContact')
				const newContact = await store.createContact(buildCreatePayload())
				if (ctx.context === 'project' && ctx.projectId != null && newContact?.Id != null) {
					await PersonApi.setMainInProjectStatus(ctx.projectId, newContact.Id)
				}
				if (ctx.refetchContacts) {
					await ctx.refetchContacts()
				}
			} else {
				await store.updateContact(props.client!, mappedValuesForSave())
			}
			emit('on-close')
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const buildCreatePayload = (): NewContact => {
	const payload = { ...(filters.value as NewContact) }
	payload.Tags = (filters.value.Tags as Tag[]).map(tag => tag.Name)
	payload.InputContainer = fullAddressContainer()
	if (ctx.context === 'client' && ctx.attachClientId != null) {
		payload.ClientId = ctx.attachClientId
	} else if (ctx.context === 'project' && selectedAttachClientId.value != null) {
		payload.ClientId = selectedAttachClientId.value
	} else {
		payload.ClientId = 0
	}
	return payload
}

const onOpen = async () => {
	try {
		isLoading.value = true
		// Структура адреса нужна обоим режимам: на редактировании — чтобы поднять
		// AddressNodeId по уровням, на создании — чтобы добрать пустые уровни в payload.
		await auxStore().getAddressContainerStructure()
		if (props.type === 'create') {
			const newContactFromLS = localStorage.getItem('newContact');
			filters.value = newContactFromLS
				? JSON.parse(newContactFromLS)
				: {
					Name: '',
					Post: '',
					Contacts: '',
					InputContainer: {
						components: [],
						activeLevel: 0
					},
					Tags: []
				};
			contactAddressChain.value = null
		} else {
			filters.value = structuredClone(props.client)
			contactAddressChain.value = addressChainToContainer(props.client?.AddressChain ?? [])
		}
		setTimeout(() => {
			isInit.value = true
		}, 100)
		await nextTick(() => props.type === 'create'
			? $nameRef.value?.focusInput()
			: undefined
		)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const checkErrors = () => {
	nameError.value = !isNameFilled.value
	postError.value = !isPostFilled.value
	contactsError.value = !isContactsFilled.value
	invalidAddressLevels.value = missingAddressLevels.value

	return nameError.value || postError.value || contactsError.value || invalidAddressLevels.value.length > 0
}

const mappedValuesForSave = () => {
	return {
		PersonId: (filters.value as Client).Id!,
		Name: filters.value.Name,
		Post: filters.value.Post,
		Contacts: filters.value.Contacts,
		InputContainer: fullAddressContainer(),
		Tags: (filters.value.Tags as Tag[]).map(tag => tag.Name)
	}
}

const showExistContacts = async () => {
	if (isContactExistArray.value.length) {
		await ctx.showExistContacts?.(
			isContactExistArray.value.map(contact => contact.Id),
			filters.value.Contacts
		)

		if (props.type === 'edit') {
			emit('on-close')
		}

		emit('exist-contact-string', filters.value.Contacts)
	}
}

watch(() => filters.value.Contacts, async (newVal) => {
	if (newVal?.length >= 8 && isInit.value) {
		isContactExistArray.value = await PersonApi.checkContactIsExist(newVal.split(' ').join(''))
		isContactExist.value = !!isContactExistArray.value.length;
	} else {
		isContactExist.value = false
		isContactExistArray.value = []
	}
})

watch(contactAddressChain, () => {
	// До первой попытки сохранения молчим. После — пересчитываем, чтобы подсветка
	// снималась с уровней по мере заполнения. Безусловный сброс тут не годится:
	// watcher'ы flush: 'pre' асинхронны, и вызов после flush() в onSave затирал бы
	// подсветку, выставленную checkErrors(), ещё до отрисовки.
	if (!invalidAddressLevels.value.length) {
		return
	}
	invalidAddressLevels.value = missingAddressLevels.value
})

watch(filters, () => {
	if (props.type === 'create') {
		localStorage.setItem('newContact', JSON.stringify(filters.value))
	}
}, {
	deep: true,
})

watch(attachClientItems, (items) => {
	if (props.type === 'create' && ctx.context === 'project' && selectedAttachClientId.value == null) {
		selectedAttachClientId.value = (items[0]?.id as number) ?? null
	}
}, { immediate: true })

onMounted(() => onOpen())

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))
</script>

<style scoped lang="scss">
.create-edit-contact {
	display: flex;
	flex-direction: column;
	height: 100%;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 0 3px;
		padding: 12px 9px;
		border-bottom: 1px solid #D2D2D2;
		box-sizing: border-box;

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 600;
			color: #4D4D4D;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		margin: 0 3px;
		padding: 8px 9px;
		border-bottom: 1px solid #D2D2D2;

		&-tags {
			display: inline-flex;
			align-items: center;

			&-add {
				margin-right: 2px;
			}

			&-item {
				font-size: 12px;
				line-height: 16px;
				color: #979797;

				&-delete {
					margin: 0 0 0 -2px;
				}
			}
		}

		&::v-deep(.q-field__append) {
			height: 32px;
		}
	}

	&__textarea {
		height: 100%;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: 16px 12px 12px;
	}

	&__search {
		width: 100%;
		margin-bottom: 8px;

		&::v-deep(.q-field__control) {
			height: 32px;
		}
	}

	&__address {
		width: 100%;
		margin-bottom: 4px;

		@each $level in (0, 1, 2, 3, 4, 5) {
			&[data-invalid-levels~="#{$level}"] {
				:deep(.section[data-key="addr_level_#{$level}"]::before),
				:deep(.section[data-key="addr_level_#{$level}"] .token:empty::before) {
					color: #FF5533;
				}
			}
		}
	}

	&__contact-exist {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		color: #3F884F;
		font-size: 12px;
		line-height: 16px;
		opacity: 0;
		margin-bottom: 2px;
		user-select: none;

		&--show {
			opacity: 1;
			user-select: auto;

			&:hover {
				text-decoration: underline;
				cursor: pointer;
			}
		}
	}
}
</style>
